import {computed, DestroyRef, effect, inject, Injectable, signal, untracked} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {BetType} from '@/api/player-api/models/bet-type'
import {BettingLimitsDto} from '@/api/player-api/models/betting-limits-dto'
import {BettingRulesDto} from '@/api/player-api/models/betting-rules-dto'
import {BetslipOperations} from '@/features/betslip/betslip-create/helpers/betslip-operations'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {BetslipData} from '@/features/betslip/betslip-create/lib/models/betslip-data'
import {BetslipSelection} from '@/features/betslip/betslip-create/lib/models/betslip-selection'
import {BetslipSystem} from '@/features/betslip/betslip-create/lib/models/betslip-system'
import {
	CappingVector,
	CappingVectorDemoLimits,
	CappingVectorInput
} from '@/features/betslip/betslip-create/test/capping-vectors/capping-vector'
import {PLACEHOLDER_LIMITS} from '@/features/betslip/betslip-create/lib/solvers/placeholder-limits'
import {STORAGE_KEYS, StorageService} from '@/services/storage.service'
import {OfferSelection} from './demo-offer'
import {LiabilityLimit, RiskInfoStatus} from './mock-betslip-risk-backend'
import {LiabilityLimitsState, PlayerLiabilityTier} from './liability-limits.state'

/**
 * The operator's Betting Limits as the betslip-demo edits them. Same fields as the demo, so a
 * vector's `demoLimits` round-trips between the two. 0 means no limit.
 */
export type WorkbenchLimits = CappingVectorDemoLimits

/** The tab the player picks; production resolves multi-single and Bet Builder from the Selections. */
export type RequestedBetType = BetType.single | BetType.combo | BetType.system

/**
 * The last value typed into the Betslip. A vector can only carry a stake or a payout
 * (`CappingVectorInput`), so the per-Option and per-Selection stakes — which production keeps on the
 * rows themselves — are replayed from those rows instead of from a recorded number.
 */
export type WorkbenchInput =
	| CappingVectorInput
	/** The global Stake per Combination, spread over every System Option. */
	| {kind: 'stakePerComb', value: number}
	/** One or more Options carry their own stake; the Betslip's stake is their sum. */
	| {kind: 'systemStakes'}
	/** Multi-single: one or more Selections carry their own stake. */
	| {kind: 'selectionStakes'}

export const DEFAULT_LIMITS: WorkbenchLimits = {
	minStake: PLACEHOLDER_LIMITS.minStake,
	// The placeholders are the VIP tier, the stake cap; the Auto-accept tier sits at half of it.
	maxStake: PLACEHOLDER_LIMITS.maxPrematchStake / 2,
	vipStakeLimit: PLACEHOLDER_LIMITS.maxPrematchStake,
	liveStakeLimit: PLACEHOLDER_LIMITS.maxLiveStake / 2,
	vipLiveStakeLimit: PLACEHOLDER_LIMITS.maxLiveStake,
	maximumPayout: PLACEHOLDER_LIMITS.maximumPayout,
	maximumTotalOdds: PLACEHOLDER_LIMITS.maximumTotalOdds,
	maxCombinations: 10000,
	maxStakeToWinMaximumPayout: false
}

/** Production counts Combinations against a number, not against "0 means no limit". */
const NO_COMBINATIONS_LIMIT = 1_000_000_000

/** The demo has no selection count limit; the offer has 33 selections. */
const MAX_SELECTIONS = 50

/** A VIP's stake is not capped; production reads 0 or unset as "the placeholder", so no cap is a number no stake reaches. */
const NO_STAKE_CAP = 1_000_000_000

/** The Betting Limits the player's tier decides: the Auto-accept limit and the Stake Limits the stake is capped at. */
const STAKE_TIER_KEYS = ['maxAcceptAmount', 'maxPrematchStakeForOnline', 'maxLiveStakeForOnline'] as const
type StakeTierKey = typeof STAKE_TIER_KEYS[number]

function isStakeTierKey(key: string): key is StakeTierKey {
	return (STAKE_TIER_KEYS as readonly string[]).includes(key)
}

/**
 * The demo's limits in the shape SiteConfigService serves — the same mapping the demo records
 * vectors with (`betslip-demo/src/app/domain/capping-vector.ts`), so the workbench and a replayed
 * vector feed production identical rules.
 */
export function toBettingRules(limits: WorkbenchLimits): BettingRulesDto {
	return {
		betslipMaxCombinations: limits.maxCombinations > 0 ? limits.maxCombinations : NO_COMBINATIONS_LIMIT,
		betslipMaxSelections: MAX_SELECTIONS,
		minStakePerCombination: 0,
		bettingLimitsDto: {
			minOnlineStake: limits.minStake,
			// P1: the Auto-accept tier. The DTO has one, so the live Auto-accept tier is only in demoLimits.
			maxAcceptAmount: limits.maxStake,
			// P1: the stake is capped at the VIP tier, which sits on Auto-accept when unset.
			maxPrematchStakeForOnline: limits.vipStakeLimit || limits.maxStake,
			maxLiveStakeForOnline: limits.vipLiveStakeLimit || limits.liveStakeLimit,
			maximumPayout: limits.maximumPayout,
			maximumTotalOdds: limits.maximumTotalOdds
		}
	}
}

/**
 * The Auto-accept limit and the caps for a player. A normal player is accepted automatically up to
 * the Limit column and capped at the VIP column, a stake between the two going for Authorization.
 * A VIP is accepted automatically up to the VIP column and not capped, a stake above it going for
 * Authorization. A VIP limit left at 0 sits on the Limit. The DTO has one Auto-accept field, the
 * prematch one, so the live Limit only stands in for an unset live VIP limit.
 */
export function stakeTiers(
	limits: WorkbenchLimits,
	tier: PlayerLiabilityTier.normal | PlayerLiabilityTier.vip
): Pick<BettingLimitsDto, StakeTierKey> {
	const prematchVip = limits.vipStakeLimit || limits.maxStake
	const liveVip = limits.vipLiveStakeLimit || limits.liveStakeLimit

	return tier === PlayerLiabilityTier.vip
		? {maxAcceptAmount: prematchVip, maxPrematchStakeForOnline: NO_STAKE_CAP, maxLiveStakeForOnline: NO_STAKE_CAP}
		: {maxAcceptAmount: limits.maxStake, maxPrematchStakeForOnline: prematchVip, maxLiveStakeForOnline: liveVip}
}

/**
 * The rules the workbench's own betslip runs on, for the player the workbench picks. "Not applied"
 * leaves the tier fields unset, so the stake falls back to the placeholder Stake Limits, as the
 * player's betslip falls back to the site's own; every other limit is the workbench's either way.
 */
export function workbenchBettingRules(limits: WorkbenchLimits, tier: PlayerLiabilityTier): BettingRulesDto {
	const rules = toBettingRules(limits)
	const others = Object.fromEntries(Object.entries(rules.bettingLimitsDto)
		.filter(([key]) => !isStakeTierKey(key)))

	return {
		...rules,
		bettingLimitsDto: tier === PlayerLiabilityTier.off ? others : {...others, ...stakeTiers(limits, tier)}
	}
}

/**
 * The site's Betting Rules with the workbench's limits laid over them, for a normal or a VIP player.
 * The Auto-accept limit and the caps always come from the player's tier (`stakeTiers`); every other
 * limit is laid over only once edited, so one left at its default keeps what the site runs on now.
 */
export function withWorkbenchLimits(
	rules: BettingRulesDto,
	limits: WorkbenchLimits,
	tier: PlayerLiabilityTier.normal | PlayerLiabilityTier.vip
): BettingRulesDto {
	const edited = toBettingRules(limits)
	const defaults = toBettingRules(DEFAULT_LIMITS)
	const changed = (Object.keys(edited.bettingLimitsDto) as (keyof BettingLimitsDto)[])
		.filter((key) => !isStakeTierKey(key))
		.filter((key) => edited.bettingLimitsDto[key] !== defaults.bettingLimitsDto[key])
	const combinationsChanged = edited.betslipMaxCombinations !== defaults.betslipMaxCombinations

	return {
		...rules,
		betslipMaxCombinations: combinationsChanged ? edited.betslipMaxCombinations : rules.betslipMaxCombinations,
		bettingLimitsDto: {
			...rules.bettingLimitsDto,
			...Object.fromEntries(changed.map((key) => [key, edited.bettingLimitsDto[key]])),
			...stakeTiers(limits, tier)
		}
	}
}

/**
 * The Betting Limits the operator is editing. Kept apart from the store, which needs the
 * betslip — the betslip's site config reads these, so they must not depend on it.
 *
 * Root rather than on the workbench, so they outlive the overlay and the player's betslip
 * (`PlayerBettingLimitsMock`) runs on the same limits the workbench edits. Kept across reloads in
 * localStorage, where only the limits that differ from the defaults are stored: `reset` brings back
 * the defaults and leaves nothing stored, and a default changed in code still reaches the rest.
 */
@Injectable({providedIn: 'root'})
export class WorkbenchLimitsState {
	private readonly player = inject(LiabilityLimitsState).playerTier

	readonly limits = signal<WorkbenchLimits>(loadWorkbenchLimits(StorageService.get(STORAGE_KEYS.devBettingLimits)))
	readonly bettingRules = computed(() => workbenchBettingRules(this.limits(), this.player()))

	constructor() {
		effect(() => {
			const changed = changedWorkbenchLimits(this.limits())
			if (Object.keys(changed).length === 0) {
				StorageService.remove(STORAGE_KEYS.devBettingLimits)
			} else {
				StorageService.set(STORAGE_KEYS.devBettingLimits, changed)
			}
		})
	}

	/** Back to the default limits, with nothing stored. */
	reset(): void {
		this.limits.set(DEFAULT_LIMITS)
	}
}

/** Saved limits laid over the defaults; a field that is not a limit, or of another type, is ignored. */
export function loadWorkbenchLimits(saved: Partial<WorkbenchLimits> | null): WorkbenchLimits {
	const valid = Object.entries(saved ?? {}).filter(([key, value]) =>
		key in DEFAULT_LIMITS && typeof value === typeof DEFAULT_LIMITS[key as keyof WorkbenchLimits])

	return {...DEFAULT_LIMITS, ...Object.fromEntries(valid)}
}

/** The limits that differ from the defaults — what is stored. */
export function changedWorkbenchLimits(limits: WorkbenchLimits): Partial<WorkbenchLimits> {
	return Object.fromEntries(Object.entries(limits)
		.filter(([key, value]) => value !== DEFAULT_LIMITS[key as keyof WorkbenchLimits]))
}

/**
 * State of the dev-tools Betslip workbench: the limits the operator is editing and the
 * isolated production betslip they feed (see `provideIsolatedBetslip`). Provided by the
 * workbench component, so it lives and dies with the overlay.
 */
@Injectable()
export class BetslipWorkbenchStore {

	private readonly betslip = inject(BetslipCreateService)
	private readonly limitsState = inject(WorkbenchLimitsState)

	readonly limits = this.limitsState.limits
	readonly bettingRules = this.limitsState.bettingRules

	/** One Liability Limit per market-outcome pair, shared with the player's betslip mock. */
	readonly liabilityLimits = inject(LiabilityLimitsState).limits

	readonly riskStatus = signal(RiskInfoStatus.ready)

	/**
	 * The betslip as production last pushed it. BetslipCreateService pushes the same mutated object
	 * every time, so the signal must not compare by reference.
	 */
	readonly data = signal<BetslipData>(this.betslip.getBetslipData(), {equal: () => false})

	readonly requested = signal<RequestedBetType>(BetType.combo)

	/** The last value the player typed — what a limit change is replayed from, and what a vector records. */
	readonly input = signal<WorkbenchInput>({kind: 'stake', value: 10})

	readonly selections = computed(() => this.data().selections)
	readonly systems = computed(() => this.data().systems)
	readonly allSystemsChecked = computed(() => this.systems().length > 0 && this.systems().every((s) => s.checked))
	readonly isSystem = computed(() => BetslipOperations.isSystemBetslip(this.data().betType))
	readonly isMultiSingle = computed(() => this.data().betType === BetType.multiSingle)

	constructor() {
		this.betslip.betslipData$
			.pipe(takeUntilDestroyed(inject(DestroyRef)))
			.subscribe((data) => this.data.set(data))

		this.betslip.clearBetslip()

		// The player picked in the workbench decides the Auto-accept limit and the caps, so a change of
		// player replays the last input on the new rules, as does a reset of the limits.
		const player = inject(LiabilityLimitsState).playerTier
		effect(() => {
			player()
			this.limits()
			untracked(() => this.replayInput())
		})
	}

	isSelected(selection: OfferSelection): boolean {
		return this.selections().some((s) => s.matchId === selection.matchId && s.oddsId === selection.oddsId)
	}

	toggle(selection: OfferSelection): void {
		if (this.isSelected(selection)) {
			this.betslip.removeSelection(selection.matchId, selection.oddsId)
		} else {
			this.betslip.addSelection({...selection})
			this.applyRequested()
		}
		this.replayInput()
	}

	remove(selection: {matchId: number, oddsId: number}): void {
		this.betslip.removeSelection(selection.matchId, selection.oddsId)
		this.replayInput()
	}

	clear(): void {
		this.betslip.clearBetslip()
	}

	request(requested: RequestedBetType): void {
		this.requested.set(requested)
		this.applyRequested()
		this.replayInput()
	}

	setStake(stake: number): void {
		if (!Number.isFinite(stake)) {
			return
		}
		this.input.set({kind: 'stake', value: Math.max(stake, 0)})
		this.betslip.setStake(Math.max(stake, 0))
	}

	setPayout(payout: number): void {
		if (!Number.isFinite(payout)) {
			return
		}
		this.input.set({kind: 'payout', value: Math.max(payout, 0)})
		this.betslip.setPayout(Math.max(payout, 0))
	}

	/** The global Stake per Combination: production spreads it over every Option. */
	setStakePerComb(stakePerComb: number): void {
		if (!Number.isFinite(stakePerComb) || !this.isSystem()) {
			return
		}
		this.input.set({kind: 'stakePerComb', value: Math.max(stakePerComb, 0)})
		this.betslip.setStakePerComb(Math.max(stakePerComb, 0))
	}

	/**
	 * One Option's own Stake per Combination. Production reads it back off the row, so the row is
	 * written first and the service called with nothing — the same double binding the main Betslip
	 * uses (`betslip-create.component.html`).
	 */
	setSystemStakePerComb(system: BetslipSystem, stakePerComb: number): void {
		if (!Number.isFinite(stakePerComb) || !this.isSystem()) {
			return
		}
		system.stakePerComb = Math.max(stakePerComb, 0)
		this.input.set({kind: 'systemStakes'})
		this.betslip.setSystemStakePerComb()
	}

	/** One Option's total stake; production divides it back into that Option's Stake per Combination. */
	setCombinationStake(system: BetslipSystem, combinationStake: number): void {
		if (!Number.isFinite(combinationStake) || !this.isSystem()) {
			return
		}
		system.combinationStake = Math.max(combinationStake, 0)
		this.input.set({kind: 'systemStakes'})
		this.betslip.setCombinationStake(system)
	}

	/** Multi-single: one Selection's own stake. The Betslip's stake becomes the sum of them. */
	setSelectionStake(selection: BetslipSelection, stake: number): void {
		if (!Number.isFinite(stake) || !this.isMultiSingle()) {
			return
		}
		this.input.set({kind: 'selectionStakes'})
		this.betslip.setSelectionStake(Math.max(stake, 0), selection.matchId, selection.oddsId)
	}

	toggleSystem(system: BetslipSystem): void {
		this.betslip.setSystem(!system.checked, system)
	}

	selectAllSystems(select: boolean): void {
		this.betslip.selectAllSystems(select)
	}

	patchLimits(patch: Partial<WorkbenchLimits>): void {
		this.limits.update((limits) => ({...limits, ...patch}))
		this.replayInput()
	}

	patchLiabilityLimit(
		pair: LiabilityLimit,
		patch: Partial<Pick<LiabilityLimit, 'liabilityLimit' | 'vipLiabilityLimit'>>
	): void {
		this.liabilityLimits.update((limits) => limits.map((limit) =>
			limit.marketId === pair.marketId && limit.outcomeKey === pair.outcomeKey ? {...limit, ...patch} : limit))
	}

	/** This betslip as a capping vector for `capping-vectors.json` — inputs only, expectations are hand-written. */
	toVector(name: string, note: string): CappingVector {
		const limits = this.limits()
		const data = this.data()

		return {
			name,
			...(note ? {note} : {}),
			recordedAt: new Date().toISOString(),
			bettingRules: this.bettingRules(),
			maxStakeToWinMaximumPayout: limits.maxStakeToWinMaximumPayout,
			demoLimits: {...limits},
			selections: [...data.selections]
				.sort((a, b) => a.sortIndex - b.sortIndex)
				.map((s) => ({
					matchId: s.matchId,
					matchName: s.matchName,
					marketId: s.marketId,
					marketLabel: s.marketLabel,
					oddsId: s.oddsId,
					outcomeLabel: s.outcomeLabel,
					odds: s.odds,
					isLiveMatch: s.isLiveMatch
				})),
			requestedBetType: this.requested(),
			systems: data.systems.filter((s) => s.checked).map((s) => s.count).sort((a, b) => a - b),
			input: vectorInput(this.input(), data.stake)
		}
	}

	/** The replay only asks for a Betslip Type once there is more than one Selection; so does the workbench. */
	private applyRequested(): void {
		if (this.selections().length > 1) {
			this.betslip.setStrategy(this.requested())
		}
	}

	/** Re-runs the betslip on the last thing typed, so a changed limit is applied to it. */
	private replayInput(): void {
		if (this.selections().length === 0) {
			return
		}

		const input = this.input()

		switch (input.kind) {
			case 'stake':
				return this.betslip.setStake(input.value)
			case 'payout':
				return this.betslip.setPayout(input.value)
			// The Betslip Type can have changed since it was typed — only a System spreads a stake
			// over Combinations, and only a Multi-single keeps a stake per Selection. Anything else
			// falls back to the stake the Betslip already carries.
			case 'stakePerComb':
				return this.isSystem() ? this.betslip.setStakePerComb(input.value) : this.replayTotalStake()
			case 'systemStakes':
				return this.isSystem() ? this.betslip.setSystemStakePerComb() : this.replayTotalStake()
			case 'selectionStakes':
				return this.isMultiSingle() ? this.replaySelectionStakes() : this.replayTotalStake()
		}
	}

	private replayTotalStake(): void {
		this.betslip.setStake(this.data().stake)
	}

	/** Re-applies the stakes the Selections already carry, so a limit change does not redistribute them. */
	private replaySelectionStakes(): void {
		for (const selection of this.data().selections) {
			this.betslip.setSelectionStake(selection.stake ?? 0, selection.matchId, selection.oddsId)
		}
	}
}

/**
 * A vector carries a typed stake or a typed payout, nothing else. A Betslip whose stake was built
 * out of per-Option or per-Selection amounts is recorded as the total they add up to; a replay
 * spreads that evenly, so it only reproduces the Betslip when the shares were even to begin with.
 */
function vectorInput(input: WorkbenchInput, stake: number): CappingVectorInput {
	return input.kind === 'stake' || input.kind === 'payout' ? input : {kind: 'stake', value: stake}
}
